/**
 * Correspondence Service Types
 *
 * Types that match the backend API structure for matter correspondences
 */

import { z } from 'zod';

// ============================================================================
// Core Correspondence Types
// ============================================================================

/**
 * Correspondence type as defined by backend.
 *
 * `collection-request` is the canonical name backend uses for what the FE
 * historically labelled `redemption`; both are accepted during the
 * Communications rollout. `acknowledgement` is auto-set on inbound emails
 * received within 10 minutes of an outbound send. `ad-hoc` is used for plain
 * rich-text emails on contact threads and ad-hoc institution emails —
 * distinct from `notification` / `collection-request` so letter-pipeline
 * affordances (signatory selection, letterhead preview, PDF download) stay
 * hidden.
 */
export type CorrespondenceType =
  | 'notification'
  | 'redemption'
  | 'collection-request'
  | 'follow-up'
  | 'reply'
  | 'acknowledgement'
  | 'ad-hoc';

/**
 * Correspondence status as defined by backend.
 * `quarantined` is the terminal send-failure state (LF-4690) — the send
 * workflow failed after dispatch and the row carries a `sendFailureReason`.
 */
export type CorrespondenceStatus =
  | 'draft'
  | 'sending'
  | 'sent'
  | 'received'
  | 'quarantined';

/**
 * Address structure
 */
export interface Address {
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
}

/**
 * File attachment
 */
export interface Attachment {
  id: number;
  displayName?: string;
  originalFilename: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  s3Key: string;
  imageS3Key: string | null;
  mimeType: string;
  extractedData: Record<string, unknown> | null;
  isCertifiedCopy: boolean;
  fileType: string;
  fileSizeKb: number | null;
}

/**
 * Institution (bank, financial organization, etc.)
 */
export interface Institution {
  id: number;
  name: string;
  email: string | null;
  hardCopyRequired: boolean;
  postalAddress: string;
  assetCollectionEmail: string | null;
  acceptsEmailForCollectionRequest: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * User info for signatory/law firm user.
 *
 * `firstName`/`lastName` are nullable — `law_firm_users.first_name` /
 * `last_name` are `nullable: true` (LF-6015/LF-6056). Render through
 * `getUserDisplayName` (`lib/utils/permissions.ts`), which falls back to
 * `email`.
 */
export interface UserInfo {
  email: string;
  firstName: string | null;
  lastName: string | null;
}

/**
 * Matter info within correspondence
 */
export interface CorrespondenceMatter {
  id: number;
  lawFirmInternalReference: string;
  status: string;
  notes: string;
  isArchived: boolean;
  decedentTitle: string;
  decedentFirstName: string;
  decedentMiddleName: string;
  decedentLastName: string;
  decedentDateOfBirth: string;
  decedentDateOfDeath: string;
  decedentAddress: Address;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deathCertificateFileId: number | null;
  willFileId: number | null;
}

/**
 * Inventory item linked to correspondence
 */
export interface InventoryItem {
  id: number;
  status: string;
  itemInfo: Record<string, unknown> | null;
  notes: string;
  estateReference: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  type: string;
}

/**
 * Validation info
 */
export interface ValidationInfo {
  errors: string[];
  warnings: string[];
  isValid: boolean;
}

/**
 * API Response type for Correspondence
 */
export interface CorrespondenceApiResponse {
  id: number;
  status: CorrespondenceStatus;
  type: CorrespondenceType;
  subject: string;
  fragments?: {
    mainContent?: string;
    quotedReplies?: string[];
  };
  correspondenceLetterBody: string | null;
  emailto: string | null;
  securityToken: string;
  requiresEmailResearch: boolean;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  matter: CorrespondenceMatter;
  inventoryItems: InventoryItem[];
  attachments: Attachment[];
  lawFirmUser: UserInfo;
  signatoryUser: UserInfo | null;
  institution: Institution;
  replies?: CorrespondenceApiResponse[];
  parentCorrespondence?: CorrespondenceApiResponse | null;
  parentCorrespondenceId?: number | null;
  validation?: ValidationInfo;
}

/**
 * Full correspondence entity
 */
export interface Correspondence {
  id: number;
  status: CorrespondenceStatus;
  type: CorrespondenceType;
  subject: string;
  body: string;
  correspondenceLetterBody: string | null;
  emailto: string | null;
  securityToken: string;
  requiresEmailResearch: boolean;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  matter: CorrespondenceMatter;
  inventoryItems: InventoryItem[];
  attachments: Attachment[];
  lawFirmUser: UserInfo;
  signatoryUser: UserInfo | null;
  institution: Institution;
  replies: Correspondence[];
  parentCorrespondence: Correspondence | null;
  parentCorrespondenceId: number | null;
  validation: ValidationInfo;
  fragments?: {
    mainContent?: string;
    quotedReplies?: string[];
  };
  /** Additional recipients on an AD_HOC draft — fan out at send time. */
  additionalRecipientContactMatterIds?: number[] | null;
  /** Per-send follow-up override (days). */
  followUpDays?: number | null;
  /**
   * Editable docx attachments generated at compose time from the precedent's
   * docx sources (WORD-9/10). Editable in Word or via the download/upload
   * fallback while the draft is unsent; finalized to PDF + attached at send.
   */
  generatedAttachments?: GeneratedAttachment[];
  /**
   * Firm-level gate for the generated-attachment Edit-in-Word CTA (active
   * M365/SPE consent). When false the FE shows the silent download/upload
   * fallback. Populated on the composer draft; false elsewhere.
   */
  wordEditingAvailable?: boolean;
}

/**
 * An editable docx attachment generated into a draft correspondence from an
 * email precedent's docx source (WORD-9/10).
 */
export interface GeneratedAttachment {
  id: number;
  /**
   * Current draft docx File id — the editable / downloadable artefact.
   * **Null when the document could not be generated**; `generationErrorCode`
   * says why.
   */
  fileId: number | null;
  /** The precedent docx source this was generated from (provenance). */
  sourceDocumentTemplateId: number | null;
  /** The source document template's name, for display. Null when hard-deleted (LF-6419). */
  name: string | null;
  /**
   * When a human last replaced the generated docx — a Word save-back or the
   * download/upload fallback. Null while it is still Vale's untouched
   * merge-filled render.
   */
  customisedAt: string | null;
  /**
   * Why the last generation produced no document — `blocked` (the matter is
   * missing data the precedent requires) or `errored` (the template failed).
   * Null once a document exists.
   */
  generationErrorCode: string | null;
  /** The merge-field tags the matter is missing, when `blocked`. */
  missingRequiredFields: string[] | null;
  /**
   * The **optional** merge fields that came back empty on a document that did
   * generate (VALE-788). Sibling of `missingRequiredFields`, which only ever
   * describes a generation that was blocked. `null` once the lawyer replaces it
   * with their own upload, which is not our render.
   */
  missingOptionalFields: string[] | null;
  /**
   * When Vale last **received** a Word change for this document — the "Synced
   * from Word N min ago" line. Null when it has never been edited in Word.
   *
   * Not interchangeable with {@link customisedAt}: an uploaded replacement
   * moves that and not this, and this is the only one that answers "is what I
   * typed in Word actually here".
   */
  lastSyncedFromWordAt: string | null;
  /** True while a Word working copy is open. With no checkout there is nothing to refresh. */
  wordCheckoutOpen: boolean;
  /**
   * True when Word is open and nothing has come back from it since it was
   * opened — Vale cannot prove the document about to be sent is the latest
   * save. Drives the pre-send confirm, which warns and never blocks.
   */
  wordSyncPending: boolean;
  /** 0-based attach order. */
  position: number;
}

/**
 * Summary for list views
 */
export interface CorrespondenceSummary {
  id: number;
  status: CorrespondenceStatus;
  type: CorrespondenceType;
  subject: string;
  emailto: string | null;
  createdAt: string;
  updatedAt: string;
  institution: Institution;
  validation: ValidationInfo;
}

// ============================================================================
// Request/Response Types
// ============================================================================

/**
 * Create correspondence request.
 *
 * `contactMatterId` is XOR with `institutionId` — exactly one must be set.
 * The Communications fan-out fields (`additionalRecipientContactMatterIds`,
 * `followUpDays`) are optional and meaningful only on draft creation; on
 * send, the backend materialises one outbound Correspondence per recipient,
 * each sharing a `fanOutGroupId`.
 */
export interface CreateCorrespondenceRequest {
  type: CorrespondenceType;
  institutionId?: number; // XOR with contactMatterId
  contactMatterId?: number; // XOR with institutionId — contact-thread sends
  parentCorrespondenceId?: number; // Required for replies
  subject?: string;
  mainContent?: string; // Backend uses mainContent, not body
  additionalRecipientContactMatterIds?: number[]; // max 20; multi-recipient draft intent
  followUpDays?: number; // 1-90; per-message override of firm default
}

/**
 * Bulk create correspondence request
 */
export interface BulkCreateCorrespondenceRequest {
  type: CorrespondenceType;
  institutionIds: number[];
}

/**
 * Update correspondence request.
 *
 * Communications draft edits use `additionalRecipientContactMatterIds` and
 * `followUpDays` here; participant fields (`institutionId` / `contactMatterId`)
 * are immutable post-create.
 */
export interface UpdateCorrespondenceRequest {
  subject?: string;
  mainContent?: string; // Email body content
  correspondenceLetterBody?: string; // PDF letter content
  emailto?: string;
  attachments?: number[]; // Array of file IDs to attach
  responseReceived?: boolean; // Mark if response has been received
  sentViaPost?: boolean;
  requiresEmailResearch?: boolean;
  additionalRecipientContactMatterIds?: number[]; // max 20
  followUpDays?: number; // 1-90
}

/**
 * Incoming email webhook request
 */
export interface IncomingEmailRequest {
  messageId: string;
  from: string;
  subject: string;
  body: string;
  to: string;
}

/**
 * List response
 */
export interface CorrespondenceListResponse {
  correspondences: Correspondence[];
  total: number;
}

// ============================================================================
// Validation Schemas
// ============================================================================

export const CorrespondenceTypeSchema = z.enum([
  'notification',
  'redemption',
  'collection-request',
  'follow-up',
  'reply',
  'acknowledgement',
  'ad-hoc'
]);

export const CreateCorrespondenceRequestSchema = z
  .object({
    type: CorrespondenceTypeSchema,
    institutionId: z.number().int().positive().optional(),
    contactMatterId: z.number().int().positive().optional(),
    parentCorrespondenceId: z.number().int().positive().optional(),
    subject: z.string().optional(),
    mainContent: z.string().optional(),
    additionalRecipientContactMatterIds: z
      .array(z.number().int().positive())
      .max(20)
      .optional(),
    followUpDays: z.number().int().min(1).max(90).optional()
  })
  .refine(
    (val) => (val.institutionId == null) !== (val.contactMatterId == null), // XOR
    {
      message:
        'Exactly one of institutionId or contactMatterId must be provided'
    }
  );

export const BulkCreateCorrespondenceRequestSchema = z.object({
  type: CorrespondenceTypeSchema,
  institutionIds: z.array(z.number().int().positive()).min(1)
});

export const UpdateCorrespondenceRequestSchema = z.object({
  subject: z.string().optional(),
  mainContent: z.string().optional(),
  correspondenceLetterBody: z.string().optional(),
  emailto: z.string().email().optional(),
  attachments: z.array(z.number()).optional(),
  responseReceived: z.boolean().optional(),
  sentViaPost: z.boolean().optional(),
  requiresEmailResearch: z.boolean().optional(),
  additionalRecipientContactMatterIds: z
    .array(z.number().int().positive())
    .max(20)
    .optional(),
  followUpDays: z.number().int().min(1).max(90).optional()
});

export const IncomingEmailRequestSchema = z.object({
  messageId: z.string(),
  from: z.string().email(),
  subject: z.string(),
  body: z.string(),
  to: z.string().email()
});

// ============================================================================
// Action Result Types
// ============================================================================

export interface CorrespondenceActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  validationErrors?: Record<string, string[]>;
}

// ============================================================================
// Mapping Functions
// ============================================================================

/**
 * Maps API response to frontend Correspondence type
 */
export function mapApiResponseToCorrespondence(
  response: CorrespondenceApiResponse
): Correspondence {
  return {
    ...response,
    body: response.fragments?.mainContent || '',
    replies: response.replies?.map(mapApiResponseToCorrespondence) || [],
    parentCorrespondence: response.parentCorrespondence
      ? mapApiResponseToCorrespondence(response.parentCorrespondence)
      : null,
    parentCorrespondenceId: response.parentCorrespondenceId ?? null,
    validation: response.validation || {
      errors: [],
      warnings: [],
      isValid: true
    }
  };
}
